import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import PhoneRotate from '@/v2/components/atoms/Icon/PhoneRotate';
import DesktopExpand from '@/v2/components/atoms/Icon/DesktopExpand';
import Text from '@/v2/components/atoms/Text';
import LowPowerWarning from '@/v2/components/atoms/LowPowerWarning';
import { StateContext } from '@/v2/state/store';
import './Device.scss';

const propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

const defaultProps = {};

const setVhVariable = () => {
    document.querySelector(':root').style.setProperty('--vh', `${window.innerHeight / 100}px`);
};

const Device = ({ children, data }) => {
    const [screenOrientation, setScreenOrientation] = useState(null);
    const [renderHeading, setRenderHeading] = useState(false);

    const deviceContainer = React.useRef();

    const store = useContext(StateContext);
    const { state, dispatch } = store;
    const { registerInterestOverlayVisible } = store.state;

    let timer = null;

    const deviceClassName = classNames('Device', {
        'Device--light': state.lightBgEnabled,
    });

    const init = () => {
        const orientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
        setScreenOrientation(orientation);
        setVhVariable();
    };

    const isTouch = () => {
        try {
            document.createEvent('TouchEvent');
            return true;
        } catch (e) {
            return false;
        }
    };

    const updateScreenOrientation = () => {
        const isInPortrait = window.matchMedia('(orientation: portrait)').matches;
        const isInLandScape = window.matchMedia('(orientation: landscape)').matches;

        if (isInPortrait) {
            if (screenOrientation !== 'portrait') dispatch({ type: 'SHOW_DEVICE_OVERLAY' });
            setScreenOrientation('portrait');
        }

        if (isInLandScape) {
            if (screenOrientation !== 'landscape') dispatch({ type: 'HIDE_DEVICE_OVERLAY' });
            setScreenOrientation('landscape');
            setVhVariable();
        }
    };

    const dispatchResizeEvent = () => {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 1);
    };

    useEffect(() => {
        init();
        const debouncedUpdateScreenOrientation = debounce(updateScreenOrientation, 100);
        const debouncedDispatchResizeEvent = debounce(dispatchResizeEvent, 100);
        window.addEventListener('orientationchange', debouncedDispatchResizeEvent);
        window.addEventListener('resize', debouncedUpdateScreenOrientation);

        timer = setTimeout(() => {
            setRenderHeading(true);
        }, 200);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('orientationchange', debouncedDispatchResizeEvent);
            window.removeEventListener('resize', debouncedUpdateScreenOrientation);
        };
    }, []);

    if (data) {
        const contentClasses = classNames({
            Device__content: true,
            'Device__content--hide': screenOrientation === 'landscape',
        });

        const containerClasses = classNames({
            Device__container: true,
            'Device__container--show': screenOrientation === 'landscape',
            'Device__container--overlayOpen': registerInterestOverlayVisible,
        });

        const rotateClasses = classNames({
            Device__rotate: true,
            'Device__rotate--show': screenOrientation === 'portrait' && isTouch(),
        });

        const expandClasses = classNames({
            Device__expand: true,
            'Device__expand--show': screenOrientation === 'portrait' && !isTouch(),
        });

        return (
            <section className={deviceClassName}>
                <div className={contentClasses} ref={deviceContainer}>
                    <div className={rotateClasses}>
                        <div className="Device__icon">
                            <PhoneRotate />
                        </div>
                        {renderHeading && (
                            <div className="Device__heading">
                                <Text className="h4">{data.text.rotate}</Text>
                            </div>
                        )}
                    </div>
                    <div className={expandClasses}>
                        <div className="Device__icon">
                            <DesktopExpand speed={0.5} />
                        </div>
                        {renderHeading && (
                            <div className="Device__heading">
                                <Text className="h4">{data.text.expand}</Text>
                            </div>
                        )}
                    </div>
                </div>

                <div className={containerClasses}>{children}</div>

                <LowPowerWarning />
            </section>
        );
    }

    return null;
};

Device.propTypes = propTypes;
Device.defaultProps = defaultProps;

export default Device;
